import {
  Button,
  Column,
  Heading,
  Row,
  Square,
  Stack,
  SvgX,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { LineOfCreditForm } from 'src/components/selfService/features/LineOfCredit/LineOfCreditForm';
import { Modal } from 'src/components/Modal/Modal';
import {
  DAYS,
  ILocFormFields,
  mapToFormValues,
  mapToPayload,
} from 'src/models/LineOfCreditModelUtils';
import {
  ILineOfCreditPayload,
  ILineOfCreditResponseType,
} from 'src/types/LineOfCredit';
import { sdk } from 'src/utilities/sdk';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { MEAL_CREDIT_ENTITY_NAME } from 'src/constants/mealCredit';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';

const defaultDays = new Array(DAYS.length).fill(false);

const defaultFormValues = {
  creditName: '',
  amount: '',
  budgetLength: '',
  date: '',
  days: defaultDays,
};

export interface ILineOfCreditModalProps {
  loc?: ILineOfCreditResponseType;
  locId: string;
  corpId: string;
  closeModal: () => void;
  onSubmit?: (budgetPayload: ILineOfCreditPayload) => void;
  isLoading?: boolean;
}

export function LineOfCreditModal({
  loc,
  locId,
  corpId,
  closeModal,
  onSubmit,
  isLoading,
}: ILineOfCreditModalProps): JSX.Element {
  const formMethods = useForm<ILocFormFields>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });

  const { handleSubmit, getValues } = formMethods;
  const [isLoadingDinerData, setIsLoadingDinerData] = useState(false);
  const isEditing = !!locId;

  const [currentFormValues, setCurrentFormValues] =
    useState<ILocFormFields>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const { isPhone } = useScreenSizes();
  const { sendTealiumEvent } = useAnalytics();

  useEffect(() => {
    if (loc) {
      setCurrentFormValues(mapToFormValues(loc));
    } else if (locId) {
      getLocInfo(locId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locId, loc]);

  useEffect(() => {
    const eventaction = locId
      ? 'edit loc pop-up module_impression'
      : 'create loc pop-up module_impression';

    sendTealiumEvent({
      eventcategory: 'self-service management tool_loc',
      eventaction,
      non_interaction: '1',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locId]);

  async function getLocInfo(locId: string) {
    try {
      setIsLoadingDinerData(true);

      const response = await sdk.http.get(
        `/corps/${corpId}/eventmetadata/${locId}`
      );
      setCurrentFormValues(mapToFormValues(response));
      setIsLoadingDinerData(false);
    } catch (error) {
      setIsLoadingDinerData(false);
      pushErrorFromAPI(error);
      closeModal();
    }
  }

  function onFormSubmit() {
    // there are no disabled fields in the values that come to submit function,
    // we need to get them using getValues from the hook forms
    onSubmit(mapToPayload(getValues()));
  }

  function setFormValidity(isValid: boolean) {
    setIsFormValid(isValid);
  }

  return (
    <Modal size="sm">
      <Square as="header" dimension="2" width="100%">
        <Button variant="tertiary" type="button" onClick={closeModal}>
          <SvgX size="24px" />
        </Button>
        <Square inset="md">
          <Heading variant="h1">
            {isEditing
              ? `Edit your ${MEAL_CREDIT_ENTITY_NAME.singular}`
              : `Create your ${MEAL_CREDIT_ENTITY_NAME.singular}`}
          </Heading>
        </Square>
      </Square>
      <Square inset="md" position="relative">
        <LineOfCreditForm
          forceMonthsToWrap
          useDatePickerModal
          isEditing={isEditing}
          values={currentFormValues}
          hideHeader
          style={null}
          notifyFormValidity={setFormValidity}
          defaultDays={defaultDays}
          formMethods={formMethods}
          isDataLoading={isLoadingDinerData}
        />

        <Square as="footer">
          <Row gutter={0}>
            <Column gutter={8} phone={12} tablet={6} desktop={6}>
              <Stack y={isPhone ? 'md' : 'none'}>
                <Button
                  {...(isLoading && { 'aria-label': 'loading' })}
                  block
                  variant="primary"
                  buttonSize="medium"
                  isLoading={isLoading}
                  disabled={!isFormValid || isLoading}
                  onClick={handleSubmit(onFormSubmit)}
                >
                  {isEditing ? 'Update' : 'Create'}
                </Button>
              </Stack>
            </Column>
            <Column gutter={8} phone={12} tablet={6} desktop={6}>
              <Button
                block
                variant="secondary"
                buttonSize="medium"
                disabled={isLoading}
                onClick={closeModal}
                data-testid="close-modal"
              >
                Cancel
              </Button>
            </Column>
          </Row>
        </Square>
      </Square>
    </Modal>
  );
}
